// AuthContext.tsx — RM-02
// Contexte d'authentification : utilisateur courant, rôle, permissions.
// Supporte dual-auth : MSAL (Azure AD) et JWT local (email/password + OTP).
// Décisions R-02, R-05, R-07 dans specs/004-dual-login-2fa/research.md.

import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import type { ReactNode } from "react";

import {
  AUTH_EXPIRED_EVENT,
  AUTH_METHOD_CHANGED_EVENT,
  AUTH_METHOD_KEY,
  LOCAL_AUTH_TOKEN_KEY,
} from "@/domain/constants/auth-constants";
import { ROLE_PERMISSIONS } from "@/domain/constants/role-permissions";
import type { ScopeLevel } from "@/domain/enums/scope-level";
import type { UserRole } from "@/domain/enums/user-role";
import type { AuthMethod } from "@/domain/types/auth";
import type { CurrentUser, CurrentUserPermission } from "@/domain/types/user";
import { logoutLocalSession } from "@/infrastructure/api/auth.api";
import apiClient from "@/infrastructure/api/client";
import {
  clearMsalRedirectFailure,
  hasMsalRedirectFailed,
} from "@/infrastructure/msal/auth-helpers";

const MSAL_LOGIN_FAILED_NOTICE =
  "La connexion Microsoft a échoué ou a été annulée. Veuillez réessayer.";
const ACCOUNT_DISABLED_NOTICE =
  "Votre compte est désactivé ou l'accès à l'application vous est refusé. Contactez un administrateur.";

function getRestrictedSiteIds(
  permission: CurrentUserPermission,
  regionId: string,
): string[] | null {
  if (permission.restrictedSiteIds != null) {
    return permission.restrictedSiteIds;
  }

  return (
    permission.regionSiteRestrictions?.find(
      (entry) => entry.regionId === regionId,
    )?.siteIds ?? null
  );
}

export interface AuthContextValue {
  currentUser: CurrentUser | null;
  isLoading: boolean;
  error: string | null;
  errorStatus: number | null;
  /** Message to display on the login screen (failed SSO, disabled account) */
  authNotice: string | null;
  /** Méthode d'authentification de la session courante */
  authMethod: AuthMethod | null;
  /** Persists the provider selected for the current browser session */
  setAuthMethod: (method: AuthMethod | null) => void;
  /** Stocke le JWT local en sessionStorage et déclenche le chargement du profil */
  setLocalToken: (token: string | null) => void;
  /** Retourne true si l'utilisateur a accès au site désigné (RM-02) */
  canAccessSite: (siteId: string, regionId?: string) => boolean;
  /** Retourne true si l'utilisateur a accès à la région désignée (RM-02) */
  canAccessRegion: (regionId: string) => boolean;
  /** Retourne true si l'utilisateur est SystemAdmin */
  isSystemAdmin: boolean;
  /** Retourne true si l'utilisateur peut accéder à l'interface d'administration */
  canAccessAdmin: boolean;
  /** Retourne true si l'utilisateur a le rôle spécifié dans le scope spécifié */
  hasRole: (
    role: UserRole,
    scopeLevel: ScopeLevel,
    scopeId?: string,
  ) => boolean;
  /** Déconnecte l'utilisateur : signal backend + purge MSAL ou JWT local */
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function getStoredAuthMethod(): AuthMethod | null {
  const method = sessionStorage.getItem(AUTH_METHOD_KEY);
  return method === "email" || method === "microsoft" ? method : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [authNotice, setAuthNotice] = useState<string | null>(() =>
    hasMsalRedirectFailed() ? MSAL_LOGIN_FAILED_NOTICE : null,
  );

  const clearAuthNotice = useCallback(() => {
    setAuthNotice(null);
    clearMsalRedirectFailure();
  }, []);

  // JWT local (auth email/password) — initialisé depuis sessionStorage (R-02)
  const [localToken, setLocalToken] = useState<string | null>(() =>
    sessionStorage.getItem(LOCAL_AUTH_TOKEN_KEY),
  );
  const [selectedAuthMethod, setSelectedAuthMethod] = useState<AuthMethod | null>(
    getStoredAuthMethod,
  );

  const updateAuthMethod = useCallback((method: AuthMethod | null) => {
    if (method) {
      sessionStorage.setItem(AUTH_METHOD_KEY, method);
    } else {
      sessionStorage.removeItem(AUTH_METHOD_KEY);
    }
    if (method === "microsoft") {
      clearAuthNotice();
    }
    if ("window" in globalThis) {
      globalThis.window.dispatchEvent(new Event(AUTH_METHOD_CHANGED_EVENT));
    }
    setSelectedAuthMethod(method);
  }, [clearAuthNotice]);

  const updateLocalToken = useCallback((token: string | null) => {
    if (token) {
      sessionStorage.setItem(LOCAL_AUTH_TOKEN_KEY, token);
      updateAuthMethod("email");
      clearAuthNotice();
    } else {
      sessionStorage.removeItem(LOCAL_AUTH_TOKEN_KEY);
    }
    if ("window" in globalThis) {
      globalThis.window.dispatchEvent(new Event("local-auth-token-changed"));
    }
    setLocalToken(token);
  }, [updateAuthMethod, clearAuthNotice]);

  // Explicit provider selection prevents a stale MSAL account from taking over a local session.
  const authMethod: AuthMethod | null =
    selectedAuthMethod ??
    (localToken ? "email" : isAuthenticated ? "microsoft" : null);

  useEffect(() => {
    if (!("window" in globalThis)) return undefined;

    const handleAuthExpired = () => {
      const activeAuthMethod = authMethod;
      if (activeAuthMethod === "email") {
        updateAuthMethod("email");
      }
      updateLocalToken(null);
      queryClient.clear();

      if (activeAuthMethod === "microsoft" && isAuthenticated) {
        void instance
          .logoutRedirect({
            account: instance.getActiveAccount() ?? undefined,
          })
          .catch(() => undefined);
      }
    };

    globalThis.window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => {
      globalThis.window.removeEventListener(
        AUTH_EXPIRED_EVENT,
        handleAuthExpired,
      );
    };
  }, [
    authMethod,
    instance,
    isAuthenticated,
    queryClient,
    updateAuthMethod,
    updateLocalToken,
  ]);

  useEffect(() => {
    // Aucune session active
    if (
      authMethod === null ||
      (authMethod === "email" && !localToken) ||
      (authMethod === "microsoft" && !isAuthenticated)
    ) {
      setIsLoading(false);
      setCurrentUser(null);
      setError(null);
      setErrorStatus(null);
      return;
    }

    setIsLoading(true);
    // GET /api/auth/me — client.ts gère le token approprié (MSAL ou JWT local)
    apiClient
      .get<CurrentUser>("/api/auth/me")
      .then((res) => {
        setCurrentUser(res.data);
        setError(null);
        setErrorStatus(null);
      })
      .catch((err: { status?: number; message?: string }) => {
        const status = typeof err?.status === "number" ? err.status : null;
        setErrorStatus(status);
        if (err?.status === 403) {
          setError(ACCOUNT_DISABLED_NOTICE);
        } else {
          setError(
            err?.message ??
              "Impossible de charger votre profil. Réessayez plus tard.",
          );
        }
        // Only authentication failures invalidate the local session.
        if (localToken && (status === 401 || status === 403)) {
          // The login screen replaces AuthGuard here, so the reason must survive the token removal.
          if (status === 403) setAuthNotice(ACCOUNT_DISABLED_NOTICE);
          updateLocalToken(null);
        }
        setCurrentUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [authMethod, isAuthenticated, localToken, updateLocalToken]);

  const isSystemAdmin =
    currentUser?.role === "SystemAdmin" && !currentUser.isPendingRoleAssignment;
  const rolePermissions = currentUser?.role
    ? ROLE_PERMISSIONS[currentUser.role]
    : null;
  const canAccessAdmin =
    !!rolePermissions &&
    !currentUser?.isPendingRoleAssignment &&
    (isSystemAdmin ||
      Object.values(rolePermissions.admin).some((access) => access !== false));

  const hasRole = (
    role: UserRole,
    scopeLevel: ScopeLevel,
    scopeId?: string,
  ): boolean => {
    if (!currentUser || currentUser.isPendingRoleAssignment) return false;
    if (isSystemAdmin) return true;
    if (!currentUser.role) return false;
    if (currentUser.role !== role) return false;
    return currentUser.permissions.some(
      (p: CurrentUserPermission) =>
        p.scopeLevel === scopeLevel &&
        (scopeId === undefined ||
          p.siteId === scopeId ||
          p.regionId === scopeId),
    );
  };

  const canAccessSite = (siteId: string, regionId?: string): boolean => {
    if (
      !currentUser ||
      currentUser.isPendingRoleAssignment ||
      !currentUser.role
    )
      return false;
    if (isSystemAdmin) return true;
    const hasNationalPermission = currentUser.permissions.some(
      (permission) => permission.scopeLevel === "National",
    );
    const hasExplicitDataRestriction = currentUser.permissions.some(
      (permission) =>
        permission.scopeLevel === "Site" || permission.scopeLevel === "Region",
    );
    if (
      hasNationalPermission ||
      (rolePermissions?.defaultScope === "National" &&
        !hasExplicitDataRestriction)
    ) {
      return true;
    }
    return currentUser.permissions.some((p: CurrentUserPermission) => {
      if (p.scopeLevel === "Site") return p.siteId === siteId;
      if (p.scopeLevel !== "Region" || p.regionId !== regionId) return false;

      const restrictedSiteIds = getRestrictedSiteIds(p, regionId);
      return (
        restrictedSiteIds === null ||
        restrictedSiteIds.length === 0 ||
        restrictedSiteIds.includes(siteId)
      );
    });
  };

  const canAccessRegion = (regionId: string): boolean => {
    if (
      !currentUser ||
      currentUser.isPendingRoleAssignment ||
      !currentUser.role
    )
      return false;
    if (isSystemAdmin) return true;
    const hasNationalPermission = currentUser.permissions.some(
      (permission) => permission.scopeLevel === "National",
    );
    const hasExplicitDataRestriction = currentUser.permissions.some(
      (permission) =>
        permission.scopeLevel === "Site" || permission.scopeLevel === "Region",
    );
    if (
      hasNationalPermission ||
      (rolePermissions?.defaultScope === "National" &&
        !hasExplicitDataRestriction)
    ) {
      return true;
    }
    return currentUser.permissions.some(
      (p: CurrentUserPermission) =>
        (p.scopeLevel === "Region" && p.regionId === regionId) ||
        (p.scopeLevel === "Site" && p.parentRegionId === regionId),
    );
  };

  const logout = useCallback(async (): Promise<void> => {
    if (authMethod === "email") {
      // Invalide la session JWT locale côté serveur (fire-and-forget)
      try {
        await logoutLocalSession();
      } catch {
        // Session peut déjà être expirée
      }
      updateAuthMethod("email");
      updateLocalToken(null);
      queryClient.clear();
      setCurrentUser(null);
      setError(null);
      return;
    }
    // Logout MSAL (Azure AD)
    try {
      await apiClient.post("/api/auth/logout");
    } catch {
      // Ignore
    }
    updateLocalToken(null);
    updateAuthMethod(null);
    queryClient.clear();
    await instance.logoutRedirect({
      account: instance.getActiveAccount() ?? undefined,
    });
  }, [authMethod, instance, queryClient, updateAuthMethod, updateLocalToken]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isLoading,
        error,
        errorStatus,
        authNotice,
        authMethod,
        setAuthMethod: updateAuthMethod,
        setLocalToken: updateLocalToken,
        canAccessSite,
        canAccessRegion,
        isSystemAdmin,
        canAccessAdmin,
        hasRole,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
